/**
 * AudienceChart.tsx — single-series line chart of the daily audience total (hand-rolled SVG;
 * the admin app uses no chart library — D-07-06).
 *
 * Follows the dataviz contract: 2px round-capped line in the accent hue, a 10% area wash,
 * hairline gridlines, ≥8px end marker with a 2px surface ring, ONE direct label (the last
 * value), text in text tokens (never the series colour), a crosshair that snaps to the
 * nearest day with a tooltip that lists value + capture coverage, keyboard arrows as the
 * hover equivalent, and a table twin rendered by the page (the "Table" toggle).
 *
 * One series → no legend box (the card title names it). Refetches keep the previous render
 * at reduced opacity (`fading`) — no skeleton, no layout jump.
 */

import * as React from 'react';
import type { AudienceSeriesPointDto } from '../../lib/api-client.js';
import {
  areaPath,
  formatCompact,
  formatDayLong,
  formatDayShort,
  formatInt,
  linePath,
  nearestIndex,
  niceDomain,
  pickLabelIndices,
  scaleLinear,
} from './chart-math.js';

export type MetricKey = 'followers' | 'subscribers' | 'fansTotal';

export const METRIC_LABELS: Record<MetricKey, string> = {
  followers: 'Followers',
  subscribers: 'Subscribers',
  fansTotal: 'Followers + subscribers',
};

const MARGIN = { top: 18, right: 64, bottom: 30, left: 52 };
const DEFAULT_HEIGHT = 280;

interface Props {
  points: AudienceSeriesPointDto[];
  metric: MetricKey;
  height?: number;
  /** True while a refetch is in flight — the chart holds its frame at reduced opacity. */
  fading?: boolean;
}

/** Observe the container width so the SVG can use real pixels (text never distorts). */
function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = React.useRef<T | null>(null);
  const [width, setWidth] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setWidth(Math.floor(e.contentRect.width));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

export function AudienceChart({ points, metric, height = DEFAULT_HEIGHT, fading = false }: Props) {
  const [wrapRef, width] = useWidth<HTMLDivElement>();
  const [hover, setHover] = React.useState<number | null>(null);

  const values = React.useMemo(() => points.map((p) => p[metric]), [points, metric]);
  const domain = React.useMemo(() => niceDomain(values, 5), [values]);

  const plotW = Math.max(0, width - MARGIN.left - MARGIN.right);
  const plotH = Math.max(0, height - MARGIN.top - MARGIN.bottom);

  const x = React.useMemo(() => {
    const n = points.length;
    if (n <= 1) return () => MARGIN.left + plotW / 2;
    return scaleLinear(0, n - 1, MARGIN.left, MARGIN.left + plotW);
  }, [points.length, plotW]);
  const y = React.useMemo(
    () => scaleLinear(domain.min, domain.max, MARGIN.top + plotH, MARGIN.top),
    [domain.min, domain.max, plotH],
  );

  const xy = React.useMemo(() => values.map((v, i) => ({ x: x(i), y: y(v) })), [values, x, y]);
  const xs = React.useMemo(() => xy.map((p) => p.x), [xy]);
  const labelIdx = React.useMemo(
    () => pickLabelIndices(points.length, Math.max(2, Math.floor(plotW / 90))),
    [points.length, plotW],
  );

  const onPointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const i = nearestIndex(xs, px);
    setHover(i >= 0 ? i : null);
  };

  const onKeyDown = (e: React.KeyboardEvent<SVGSVGElement>) => {
    if (points.length === 0) return;
    if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
      e.preventDefault();
      setHover((h) => {
        const cur = h ?? (e.key === 'ArrowRight' ? -1 : points.length);
        const next = e.key === 'ArrowRight' ? cur + 1 : cur - 1;
        return Math.min(points.length - 1, Math.max(0, next));
      });
    } else if (e.key === 'Escape') {
      setHover(null);
    }
  };

  const last = points.length > 0 ? points[points.length - 1]! : null;
  const hovered = hover !== null ? points[hover] ?? null : null;
  const summaryLabel =
    last === null
      ? 'No data'
      : `${METRIC_LABELS[metric]} over ${points.length} day${points.length === 1 ? '' : 's'}, ` +
        `from ${formatInt(points[0]![metric])} on ${formatDayLong(points[0]!.day)} to ${formatInt(last[metric])} on ${formatDayLong(last.day)}`;

  // Tooltip placement: flip to the left of the crosshair in the right half.
  const tipLeft = hovered !== null && hover !== null ? xs[hover]! : 0;
  const flip = width > 0 && tipLeft > width * 0.6;

  return (
    <div
      ref={wrapRef}
      className="relative w-full select-none"
      style={{ opacity: fading ? 0.55 : 1, transition: 'opacity 150ms ease' }}
    >
      {width > 0 && (
        <svg
          width={width}
          height={height}
          role="img"
          aria-label={summaryLabel}
          tabIndex={0}
          className="block outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
          onPointerMove={onPointerMove}
          onPointerLeave={() => setHover(null)}
          onKeyDown={onKeyDown}
        >
          {/* Gridlines + y ticks (hairline, recessive) */}
          {domain.ticks.map((t) => (
            <g key={t}>
              <line
                x1={MARGIN.left}
                x2={MARGIN.left + plotW}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--color-border)"
                strokeWidth={1}
                shapeRendering="crispEdges"
              />
              <text
                x={MARGIN.left - 8}
                y={y(t)}
                textAnchor="end"
                dominantBaseline="middle"
                fontSize={11}
                fill="var(--color-text-muted)"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatCompact(t)}
              </text>
            </g>
          ))}

          {/* X labels */}
          {labelIdx.map((i) => (
            <text
              key={i}
              x={x(i)}
              y={height - 8}
              textAnchor={i === 0 && points.length > 1 ? 'start' : i === points.length - 1 && points.length > 1 ? 'end' : 'middle'}
              fontSize={11}
              fill="var(--color-text-muted)"
            >
              {formatDayShort(points[i]!.day)}
            </text>
          ))}

          {/* Area wash + line */}
          {xy.length >= 2 && (
            <>
              <path d={areaPath(xy, MARGIN.top + plotH)} fill="var(--color-accent)" fillOpacity={0.1} />
              <path
                d={linePath(xy)}
                fill="none"
                stroke="var(--color-accent)"
                strokeWidth={2}
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            </>
          )}

          {/* Crosshair */}
          {hover !== null && hovered !== null && (
            <line
              x1={xs[hover]}
              x2={xs[hover]}
              y1={MARGIN.top}
              y2={MARGIN.top + plotH}
              stroke="var(--color-text-subtle)"
              strokeWidth={1}
              shapeRendering="crispEdges"
            />
          )}

          {/* Hovered point marker (2px surface ring) */}
          {hover !== null && hovered !== null && (
            <circle
              cx={xs[hover]}
              cy={xy[hover]!.y}
              r={5}
              fill="var(--color-accent)"
              stroke="var(--color-bg-card)"
              strokeWidth={2}
            />
          )}

          {/* End marker + the one direct label */}
          {last !== null && xy.length > 0 && (
            <>
              <circle
                cx={xy[xy.length - 1]!.x}
                cy={xy[xy.length - 1]!.y}
                r={4}
                fill="var(--color-accent)"
                stroke="var(--color-bg-card)"
                strokeWidth={2}
              />
              <text
                x={xy[xy.length - 1]!.x + 10}
                y={xy[xy.length - 1]!.y}
                dominantBaseline="middle"
                fontSize={12}
                fontWeight={600}
                fill="var(--color-text)"
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {formatInt(last[metric])}
              </text>
            </>
          )}
        </svg>
      )}

      {/* Tooltip — HTML so it can wrap and use the theme tokens */}
      {hover !== null && hovered !== null && width > 0 && (
        <div
          role="status"
          className="pointer-events-none absolute top-2 z-10 rounded border border-border bg-bg-card px-2.5 py-2 text-xs shadow-sm"
          style={flip ? { right: width - tipLeft + 10 } : { left: tipLeft + 10 }}
        >
          <div className="text-base font-semibold text-text" style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatInt(hovered[metric])}
          </div>
          <div className="text-text-muted">{formatDayLong(hovered.day)}</div>
          <div className="mt-1 flex items-center gap-1.5 text-text-muted">
            <span className="inline-block h-0.5 w-3 rounded" style={{ background: 'var(--color-accent)' }} />
            {METRIC_LABELS[metric]}
          </div>
          <div className="text-text-subtle">
            {hovered.accountsFresh}/{hovered.accountsTotal} captured that day
            {hovered.accountsCovered > hovered.accountsFresh
              ? `, ${hovered.accountsCovered - hovered.accountsFresh} carried forward`
              : ''}
          </div>
        </div>
      )}
    </div>
  );
}
